import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonContent,
  IonHeader,
  IonIcon,
  IonItemGroup,
  IonList,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonModal,
} from '@ionic/react';
import { useCampaignStore, useMutations } from '@lidea/shared/data-access/core';
import { useMe } from '@lidea/shared/data-access/users';
import { Collapse } from '@mantine/core';
import { useNetwork } from '@mantine/hooks';
import {
  IconPhoto,
  IconRefresh,
  IconTrash,
} from '@tabler/icons-react';
import { onlineManager } from '@tanstack/react-query';
import { close } from 'ionicons/icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OfflineCacheStatusBlock } from './components/offline-cache-status/offline-cache-status';
import { ClearMutationsButton } from './components/clear-mutations-button/clear-mutations-button';
import { FieldItem } from './components/field-item/field-item';
import { MutationItem } from './components/mutation-item/mutation-item';
import { OfflineScopePicker } from './components/offline-scope-picker/offline-scope-picker';
import { SyncStatusStrip } from './components/sync-status-strip/sync-status-strip';
import { SetupCacheControls } from './hooks/setup-cache-controls';
import { enterManualOffline, exitManualOffline } from './manual-offline';
import ui from './offline-ui.module.css';
import {
  getOfflineScopeSession,
  OfflineScopeSession,
} from './sync-job/offline-scope-session';
import { OFFLINE_CACHE_CLEARED_EVENT } from './sync-job/clear-offline-cache';
import { resolveOfflineCacheStatus } from './sync-job/offline-cache-status';
import { isResumableSyncPhase } from './sync-job/types';
import { isWideOfflineRole } from './sync-job/wide-roles';

export const OFFLINE_PLOTS_STORAGE_KEY = 'offline_plots';

/* eslint-disable-next-line */
export interface OfflineProps extends SetupCacheControls {
  dismiss: () => void;
}

export function Offline({
  dismiss,
  startAutoSync,
  startSyncWithImages,
  resumeJob,
  clearOfflineData,
  job,
  isSettingUpCache,
}: OfflineProps) {
  const { t } = useTranslation(['common'], { useSuspense: false });
  const me = useMe();
  const campaignId = useCampaignStore((s) => s.campaignId);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [isOnline, setIsOnline] = useState(true);
  const network = useNetwork();
  const [scopeSession, setScopeSession] = useState<OfflineScopeSession | null>(
    () => getOfflineScopeSession()
  );

  const wideRole = isWideOfflineRole(me.data?.role);
  const wideRoleManualSync = isWideOfflineRole(me.data?.role);
  const [cacheEpoch, setCacheEpoch] = useState(0);

  const managerId = wideRole ? scopeSession?.managerId : undefined;

  const cacheStatus = useMemo(
    () =>
      resolveOfflineCacheStatus({
        campaignId,
        managerId,
        wideRole,
        job,
        isSettingUpCache,
      }),
    [campaignId, managerId, wideRole, job, isSettingUpCache, cacheEpoch]
  );

  const refreshScopeSession = () => {
    setScopeSession(getOfflineScopeSession());
  };

  const [presentPicker, dismissPicker] = useIonModal(OfflineScopePicker, {
    userRole: me.data?.role ?? '',
    campaignId: campaignId ?? 0,
    onClose: () => dismissPicker(),
    startAutoSync,
    onScopeSaved: refreshScopeSession,
  });

  const openScopePicker = () => {
    presentPicker({
      cssClass: 'page-modal',
      componentProps: {
        initialScope: getOfflineScopeSession(),
      },
    });
  };

  const {
    pendingMutations,
    pausedMutations,
    succeededMutations,
    failedMutations,
    affectedFields,
    mutations,
  } = useMutations();

  const toggleOnline = () => {
    if (!onlineManager.isOnline() && navigator.onLine) {
      exitManualOffline();
    } else if (onlineManager.isOnline()) {
      enterManualOffline();
    }
  };

  useEffect(() => {
    setIsOnline(onlineManager.isOnline());
    const unsubscribe = onlineManager.subscribe((online) => {
      setIsOnline(online);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const onCacheCleared = () => refreshScopeSession();
    window.addEventListener(OFFLINE_CACHE_CLEARED_EVENT, onCacheCleared);
    return () => {
      window.removeEventListener(OFFLINE_CACHE_CLEARED_EVENT, onCacheCleared);
    };
  }, []);

  const handleSynchronizeCache = async (includeHeatmaps: boolean) => {
    const role = me.data?.role;
    if (!role || !campaignId) {
      return;
    }

    if (job && isResumableSyncPhase(job.status)) {
      await resumeJob();
      return;
    }

    const baseParams = {
      campaignId,
      userRole: role,
    };

    if (isWideOfflineRole(role)) {
      if (!scopeSession) {
        openScopePicker();
        return;
      }
      const params = {
        ...baseParams,
        managerId: scopeSession.managerId,
        managerLabel: scopeSession.managerLabel,
      };
      if (includeHeatmaps) {
        await startSyncWithImages(params);
      } else {
        await startAutoSync(params);
      }
      return;
    }

    if (includeHeatmaps) {
      await startSyncWithImages(baseParams);
    } else {
      await startAutoSync(baseParams);
    }
  };

  const syncDisabled = !network.online || isSettingUpCache;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t('common:offline_modal_title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={dismiss}>
              <IonIcon icon={close} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className={ui.page}>
        {isSettingUpCache && job ? (
          <SyncStatusStrip
            job={job}
            isRunning={isSettingUpCache}
            variant="inline"
          />
        ) : null}

        <div className={ui.section}>
          <p className={ui.sectionTitle}>
            {t('common:sync_job.section_connection')}
          </p>
          <div className={ui.statusRow}>
            <span className={ui.statusBadge}>
              <span
                className={`${ui.statusDot} ${
                  isOnline ? ui.statusDotOnline : ui.statusDotOffline
                }`}
              />
              {isOnline ? t('common:online_mode') : t('common:offline_mode')}
            </span>
            <IonButton
              color={isOnline ? 'medium' : 'primary'}
              disabled={!network.online}
              size="small"
              onClick={toggleOnline}
            >
              {isOnline ? t('common:go_offline') : t('common:go_online')}
            </IonButton>
          </div>
        </div>

        {wideRole ? (
          <div className={ui.section}>
            <IonCard className={ui.card}>
              <IonCardHeader>
                <IonCardSubtitle>
                  {t('common:sync_job.section_scope')}
                </IonCardSubtitle>
                <IonCardTitle>
                  {scopeSession
                    ? scopeSession.managerLabel
                    : t('common:sync_job.no_scope_short')}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent className={ui.cardContent}>
                {!scopeSession ? (
                  <p className={ui.hint}>{t('common:sync_job.no_scope_hint')}</p>
                ) : null}
                <IonButton
                  expand="block"
                  fill="outline"
                  onClick={openScopePicker}
                >
                  {scopeSession
                    ? t('common:sync_job.change_scope_button')
                    : t('common:sync_job.choose_scope_button')}
                </IonButton>
              </IonCardContent>
            </IonCard>
          </div>
        ) : null}

        <div className={ui.section}>
          <IonCard className={ui.card}>
            <IonCardHeader>
              <IonCardSubtitle>
                {t('common:sync_job.section_data')}
              </IonCardSubtitle>
              <IonCardTitle>{t('common:sync_job.section_data_title')}</IonCardTitle>
            </IonCardHeader>
            <IonCardContent className={ui.cardContent}>
              <OfflineCacheStatusBlock status={cacheStatus} />
              <p className={ui.hint}>
                {wideRoleManualSync
                  ? t('common:sync_job.control_center_sync_hint_wide')
                  : t('common:sync_job.control_center_sync_hint')}
              </p>
              <div className={ui.buttonStack}>
                <IonButton
                  disabled={syncDisabled}
                  expand="block"
                  onClick={() => handleSynchronizeCache(false)}
                >
                  <IconRefresh size={18} stroke={1.75} style={{ marginRight: 8 }} />
                  {t('common:sync_job.sync_resync_button')}
                </IonButton>
                <IonButton
                  disabled={syncDisabled}
                  expand="block"
                  fill="outline"
                  onClick={() => handleSynchronizeCache(true)}
                >
                  <IconPhoto size={18} stroke={1.75} style={{ marginRight: 8 }} />
                  {t('common:sync_job.sync_with_images_button')}
                </IonButton>
                <IonButton
                  color="danger"
                  disabled={isSettingUpCache}
                  expand="block"
                  fill="clear"
                  onClick={() => clearOfflineData()}
                >
                  <IconTrash size={18} stroke={1.75} style={{ marginRight: 8 }} />
                  {t('common:sync_job.clear_cache_button')}
                </IonButton>
              </div>
            </IonCardContent>
          </IonCard>
        </div>

        <div className={ui.mutationsHeader}>
          <p className={ui.sectionTitle} style={{ margin: 0 }}>
            {t('common:sync_job.section_mutations')}
          </p>
          {mutations.length > 0 ? (
            <ClearMutationsButton>
              {t('common:clear_mutations')}
            </ClearMutationsButton>
          ) : null}
        </div>

        {mutations.length === 0 ? (
          <p className={ui.mutationsEmpty}>
            {t('common:sync_job.mutations_empty')}
          </p>
        ) : (
          affectedFields.map((fieldId) => {
            const pending = pendingMutations.filter(
              (mutation) =>
                mutation.state.context?.metadata?.fieldId === fieldId
            );
            const paused = pausedMutations.filter(
              (mutation) =>
                mutation.state.context?.metadata?.fieldId === fieldId
            );
            const succeeded = succeededMutations.filter(
              (mutation) =>
                mutation.state.context?.metadata?.fieldId === fieldId
            );
            const errors = failedMutations.filter(
              (mutation) =>
                mutation.state.context?.metadata?.fieldId === fieldId
            );
            const lastUpdated = succeeded.length
              ? succeeded[0].state.submittedAt
              : undefined;
            const expanded = collapsed[fieldId] !== false;

            return (
              <IonItemGroup key={fieldId}>
                <FieldItem
                  expanded={expanded}
                  fieldId={fieldId}
                  lastUpdated={lastUpdated}
                  status={
                    pending.length > 0 || paused.length > 0
                      ? 'pending'
                      : succeeded.length > 0
                        ? 'success'
                        : errors.length > 0
                          ? 'error'
                          : 'success'
                  }
                  onClick={() =>
                    setCollapsed((prev) => ({
                      ...prev,
                      [fieldId]:
                        prev[fieldId] === undefined ? false : !prev[fieldId],
                    }))
                  }
                />
                <Collapse in={expanded}>
                  <IonList style={{ padding: 0 }}>
                    {errors.map((mutation) => (
                      <MutationItem
                        key={mutation.mutationId}
                        fieldId={fieldId}
                        mutation={mutation}
                        status="error"
                      />
                    ))}
                    {paused.map((mutation) => (
                      <MutationItem
                        key={mutation.mutationId}
                        fieldId={fieldId}
                        mutation={mutation}
                        status="paused"
                      />
                    ))}
                    {pending.map((mutation) => (
                      <MutationItem
                        key={mutation.mutationId}
                        fieldId={fieldId}
                        mutation={mutation}
                        status="pending"
                      />
                    ))}
                    {succeeded.map((mutation) => (
                      <MutationItem
                        key={mutation.mutationId}
                        fieldId={fieldId}
                        mutation={mutation}
                        status="success"
                      />
                    ))}
                  </IonList>
                </Collapse>
              </IonItemGroup>
            );
          })
        )}
      </IonContent>
    </IonPage>
  );
}

export default Offline;
